import { Heading } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBackToChat } from '@/hooks/useBackToChat';
import { SpaceLayout } from '@/layouts/SpaceLayout';
import { trackEvent } from '@/utils/fullstory';

import type { Campaign } from '../../../../bridge/types/campaign.ts';

import { CampaignLibrary } from './CampaignLibrary';
import { useCampaigns } from './useCampaigns';

// Strategies moved out of this space into the persistent Chat strategy dock
// (see spaces/chat/strategy-dock/StrategyDock). CampaignSpace is now a single
// Campaigns view — no tabs.
export function CampaignSpace() {
	const bridgeApi = useBridgeApi();
	const { campaigns, metrics, metricsError } = useCampaigns();

	const handleCampaignClick = useCallback(
		(campaign: Campaign) => {
			const processId = campaign.process_ids?.[0];
			if (!processId) return;
			trackEvent('Campaign Library Item Clicked', {
				campaign_id: campaign.id,
				process_id: processId,
			});
			bridgeApi.sendAction({
				type: 'do_create_chat',
				chat_type: 'artifact_review',
				initial_message: `Show me the ${campaign.title} campaign`,
				linked_item: {
					type: 'process',
					id: processId,
					metadata: {
						title: campaign.title || 'Review Campaign',
						description: campaign.goal || undefined,
						artifact_type: 'campaign',
					},
				},
			});
		},
		[bridgeApi],
	);

	const handleBack = useBackToChat();

	const handleCreateCampaign = useCallback(() => {
		trackEvent('Campaign Creation Opened');
		bridgeApi.sendAction({
			type: 'do_create_chat',
			initial_message: 'I want to create a new campaign',
		});
	}, [bridgeApi]);

	return (
		<SpaceLayout title="Campaigns" onBack={handleBack} fillHeight>
			<div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
				<div className="flex-shrink-0 pb-3">
					<div className="flex flex-col gap-1 pb-6">
						<span className="text-sm text-gray-500">Campaigns</span>
						<Heading
							level={1}
							styles={style({
								font: 'heading',
								fontWeight: 'bold',
								fontSize: 'heading-lg',
							})}
						>
							Campaigns
						</Heading>
					</div>
				</div>
				<div className="mt-6 flex min-h-0 flex-1 flex-col overflow-y-auto pb-8">
					<CampaignLibrary
						campaigns={campaigns}
						metrics={metrics}
						metricsError={metricsError}
						onCampaignClick={handleCampaignClick}
						onCreateCampaign={handleCreateCampaign}
					/>
				</div>
			</div>
		</SpaceLayout>
	);
}
